import React, { useState } from "react";
import { Button, Form, Input, message, Progress } from "antd";
import websiteLogo from "../../images/logo.png";
import { signUp } from "api/auth.api";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { notificationController } from "components/Common/Notification/Notification";

const SignUp = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false); // Loading state
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const key = searchParams.get("key");
  const role = searchParams.get('role');
  const [signUpSuccess, setSignUpSuccess] = useState(false);

  const handleFinish = async (values: any) => {
    setLoading(true);
    try {
      const { confirmPassword, ...formData } = values;

      // Attach token if available
      if (searchParams.get("token")) {
        formData.token = searchParams.get("token");
      }

      if (key === "marketing_campaign") {
        formData.source_of_referral = "MARKETING_CAMPAIGN";
      }

      await signUp(formData).then(
        (res) => {
          notificationController.success({
            message: "Account created successfully!",
            description: "You can now login to your account.",
          });
          navigate("/thank-you");
          // message.success("Account created successfully!");
          // if (key === "marketing_campaign") {
          //   setSignUpSuccess(true);
          // } else {
          //   navigate("/auth/login");
          //   form.resetFields();
          // }
        },
        (error) => {
          console.error("Error:", error);
          notificationController.error({
            message: "Error",
            description: error.response.data.message
              ? error.response.data.message
              : "Something went wrong!",
          });
        }
      );
    } catch (error) {
      notificationController.error({
        message: "Something went wrong!",
        description: "Please try again later.",
      });
      console.error("Error:", error);
      //  message.error("Something went wrong!");
    } finally {
      setLoading(false);
    }
  };

  const [passwordStrength, setPasswordStrength] = useState(0);
  const [password, setPassword] = useState("");

  // Function to calculate password strength
  const calculateStrength = (password: string) => {
    let strength = 0;
    if (password.length >= 1) strength += 4;
    if (password.length >= 3) strength += 7;
    if (password.length >= 6) strength += 7;
    if (password.length >= 8) strength += 7; // Length
    if (/[A-Z]/.test(password)) strength += 25; // Uppercase letters
    if (/[0-9]/.test(password)) strength += 25; // Numbers
    if (/[^A-Za-z0-9]/.test(password)) strength += 25; // Special characters
    return strength;
  };

  // Handle password input change
  const handlePasswordChange = (e: any) => {
    const password = e.target.value;
    setPassword(password);
    const strength = calculateStrength(password);
    setPasswordStrength(strength);
  };

  const passwordConditions = [
    { label: "At least 8 characters", test: (password: any) => password.length >= 8 },
    { label: "At least one uppercase letter", test: (password: any) => /[A-Z]/.test(password) },
    { label: "At least one lowercase letter", test: (password: any) => /[a-z]/.test(password) },
    { label: "At least one number", test: (password: any) => /\d/.test(password) },
    {
      label: "At least one special character",
      test: (password: any) => /[!@#$%^&*]/.test(password),
    },
  ];

  const handleFinishFailed = (errorInfo: any) => {
    console.error("Form submission failed:", errorInfo);
    message.error("Please check the form fields and try again.");
  };

  // useEffect(() => {
  //   navigate("/thank-you");
  // }, [signUpSuccess, key, navigate]);

  return (
    <>
      <div className="flex min-h-full flex-col justify-center px-6 py-12 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-sm">
          <img className="mx-auto h-10 w-auto" src={websiteLogo} alt="Your Company" />
          <h2 className="mt-10 text-center text-2xl font-bold leading-9 tracking-tight text-gray-900">
            Create a new account
          </h2>
        </div>

        <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-sm">
          <Form
            form={form}
            layout="vertical"
            onFinish={handleFinish}
            onFinishFailed={handleFinishFailed}
            initialValues={{
              first_name: "",
              last_name: "",
              email: "",
              password: "",
            }}
            requiredMark={false}
          >
            {/* First Name Field */}
            <Form.Item
              label={
                <label className="block text-sm font-medium leading-6 text-gray-900">
                  First Name<span style={{ color: "red", marginLeft: "4px" }}>*</span>
                </label>
              }
              name="first_name"
              rules={[
                { required: true, message: "Please enter your first name" },
                { min: 3, message: "Name must be at least 3 characters long" },
              ]}
            >
              <Input size="large" placeholder="Enter your first name" />
            </Form.Item>

            {/* Last Name Field */}
            <Form.Item
              label={
                <label className="block text-sm font-medium leading-6 text-gray-900">
                  Last Name<span style={{ color: "red", marginLeft: "4px" }}>*</span>
                </label>
              }
              name="last_name"
              rules={[
                { required: true, message: "Please enter your last name" },
                { min: 3, message: "Name must be at least 3 characters long" },
              ]}
            >
              <Input size="large" placeholder="Enter your last name" />
            </Form.Item>

            {/* Email Field */}
            <Form.Item
              label={
                <label className="block text-sm font-medium leading-6 text-gray-900">
                  Email Address<span style={{ color: "red", marginLeft: "4px" }}>*</span>
                </label>
              }
              name="email"
              rules={[
                { required: true, message: "Please enter your email address" },
                { type: "email", message: "Please enter a valid email address" },
              ]}
            >
              <Input size="large" type="email" placeholder="Enter your email" />
            </Form.Item>

            {/* Password Field */}
            <Form.Item
              label={
                <label className="block text-sm font-medium leading-6 text-gray-900">
                  Password<span style={{ color: "red", marginLeft: "4px" }}>*</span>
                </label>
              }
              name="password"
              rules={[
                { required: true, message: "Please enter your password" },
                { min: 6, message: "Password must be at least 6 characters long" },
              ]}
            >
              <Input.Password
                size="large"
                placeholder="Enter your password"
                onChange={handlePasswordChange}
              />
            </Form.Item>
            {/* Password Strength Indicator */}
            {passwordStrength > 0 && (
              <div
                style={{
                  border: `2px solid ${
                    passwordStrength <= 50 ? "red" : passwordStrength < 100 ? "#fac30c" : "green"
                  }`,
                  borderRadius: "8px", // Optional: For rounded corners
                  // backgroundImage: "linear-gradient(white, white), linear-gradient(to right, #1D4ED8, #60A5FA, #EC4899)", // Inner white and gradient border
                  // backgroundOrigin: "border-box", // Ensures the gradient stays outside
                  // backgroundClip: "padding-box, border-box", // Clips the inner white and gradient border
                  padding: "10px", // Adjust padding as needed
                  marginBottom: "20px",
                }}
              >
                <div style={{ marginBottom: "16px" }}>
                  <Progress
                    percent={passwordStrength}
                    showInfo={false}
                    status={
                      passwordStrength <= 50
                        ? "exception"
                        : passwordStrength < 100
                        ? "normal"
                        : "success"
                    }
                    // strokeColor={{
                    //   '0%': '#1D4ED8', // Blue (from-primary)
                    //   '50%': '#60A5FA', // Blue (via-blue-400)
                    //   '100%': '#EC4899', // Pink (to-pink-500)
                    // }}
                    strokeColor={
                      passwordStrength <= 50
                        ? "red" // Red for weak
                        : passwordStrength < 100
                        ? "#fac30c" // Yellow for medium
                        : "green" // Green for strong
                    }
                  />
                  <span
                    style={{
                      color:
                        passwordStrength <= 50
                          ? "red"
                          : passwordStrength < 100
                          ? "#fac30c"
                          : "green",
                      fontWeight: "bold",
                    }}
                  >
                    {passwordStrength <= 50 ? "Weak" : passwordStrength < 100 ? "Medium" : "Strong"}
                  </span>
                </div>
                <hr />
                <div className="mt-2">
                  {passwordConditions.map((condition, index) => (
                    <p
                      key={index}
                      style={{ color: condition.test(password) ? "green" : "red" }}
                      className={`text-sm`}
                    >
                      {condition.label}
                    </p>
                  ))}
                </div>
              </div>
            )}
            {/* Confirm Password Field */}
            <Form.Item
              label={
                <label className="block text-sm font-medium leading-6 text-gray-900">
                  Confirm Password<span style={{ color: "red", marginLeft: "4px" }}>*</span>
                </label>
              }
              name="confirmPassword"
              dependencies={["password"]}
              rules={[
                { required: true, message: "Please confirm your password" },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue("password") === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error("Passwords do not match"));
                  },
                }),
              ]}
            >
              <Input.Password size="large" placeholder="Confirm your password" />
            </Form.Item>

            {/* Company Name Field */}
            <Form.Item
              label={
                <label className="block text-sm font-medium leading-6 text-gray-900">
                  Company Name<span style={{ color: "red", marginLeft: "4px" }}>*</span>
                </label>
              }
              name="company_name"
              rules={[
                { required: true, message: "Please enter your company name" },
                { min: 2, message: "Company name must be at least 2 characters long" },
              ]}
            >
              <Input size="large" placeholder="Enter your company name" />
            </Form.Item>

            {/* Submit Button */}
            <Form.Item>
             {role && <Button
                size="large"
                type="primary"
                htmlType="submit"
                className="w-full"
                loading={loading} // Show loader on button
              >
                Sign Up
              </Button>}
            </Form.Item>
            <Button
              size="large"
              loading={loading}
              onClick={() => navigate("/auth/login")}
              className="px-3 py-2 rounded-md w-[100%]"
            >
              Log In
            </Button>
          </Form>
        </div>
      </div>
    </>
  );
};

export default SignUp;
